import {
	AddressItemType,
	GetItemCounts,
	InfiniteData,
	PaginatedResponseType,
	useInfiniteAddresses,
} from '@ukpostbox/client-data';
import {
	AlertBanner,
	Colour,
	ComboBoxControl,
	ComboBoxItemDisplayEnum,
	ComboBoxTypeEnum,
	FlexGap,
	SelectItemType,
	StackVertical,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { GetArrayIndexByKeyValue } from '@ukpostbox/helpers';
import React, { useCallback, useEffect, useState } from 'react';
import { AddAddressTypeButtons } from './AddAddress/AddAddressTypeButtons';
import { AddressControlType, AddressListItemType } from './Types/AddressTypes';

function AddressControl(props: AddressControlType) {
	const { t } = useTranslation();

	const {
		status,
		data,
		error,
		dataUpdatedAt,
		fetchNextPage,
		isRefetching,
		isStale,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteAddresses(50);

	const [addressOptions, setAddressOptions] = useState<Array<any>>([]);
	const [addressItemCounts, setAddressItemCounts] = useState<any>({ loaded: 0, total: 0 });

	const BuildFullAddresses = (e: Array<number>) => {
		const fullAddresses: Array<any> = [];

		data?.pages.map(
			(page: any) =>
				page?.data &&
				page.data.forEach((address: AddressListItemType) => {
					if (e.includes(address.value)) {
						fullAddresses.push(address);
					}
				}),
		);

		return fullAddresses;
	};

	const HandleForwardFullAddresses = (e: Array<number>) => {
		if (props.forwardFullAddressesCallback) {
			props.forwardFullAddressesCallback(BuildFullAddresses(e));
		}
	};

	const HandleValueChanged = (e: any) => {
		if (typeof e === 'object' && props.forwardValuesCallback) {
			HandleForwardFullAddresses(e);
			props.forwardValuesCallback(e);
		}

		if (typeof e === 'number' && props.forwardValueCallback) {
			HandleForwardFullAddresses([e]);
			props.forwardValueCallback(e);
		}
	};

	const HandleItemsChanged = useCallback(
		(e: SelectItemType, forwardedData?: Array<any>) => {
			if (props.forwardItemCallback) {
				let addressItem;

				if (forwardedData) {
					forwardedData.forEach((address) => {
						if (address.value === e) {
							addressItem = {
								label: address.label,
								value: address.value,
								object: {
									addressId: address.addressId || address.value,
									addressString: address.addressString || address.label,
									name: address.name || '',
									businessName: address.businessName || '',
									propertyName: address.propertyName || '',
									addressLine1: address.addressLine1 || '',
									addressLine2: address.addressLine2 || '',
									addressLine3: address.addressLine3 || '',
									postCode: address.postCode || '',
									countryCode: address.countryCode || address.label.split(',').pop().trim(),
									phoneNo: address.phoneNo || '',
									emailAddress: address.emailAddress || '',
								},
							};
						}
					});
				} else {
					data?.pages.forEach(
						(page) =>
							page?.data &&
							page.data.forEach((address) => {
								if (address.object.addressId === e.value) {
									addressItem = address;
								}
							}),
					);
				}

				if (addressItem) {
					props.forwardItemCallback(addressItem);
				}
			}
		},
		[data, addressOptions],
	);

	const MapAddressOptions = (newData: InfiniteData<PaginatedResponseType>) => {
		setAddressItemCounts(GetItemCounts(newData));

		const newAddresses = newData?.pages.map(
			(page) => page?.data && page.data.map((address) => ({ label: address.label, value: address.value })),
		)[0];

		let shapedExtraAddress: AddressItemType | undefined;

		if (props.extraAddressData || props.extraForwardingAddressData) {
			const existingIndex = GetArrayIndexByKeyValue(
				newAddresses,
				'value',
				props.extraForwardingAddressData
					? props.extraForwardingAddressData.addressId
					: props.extraAddressData?.object.addressId,
			);

			if (existingIndex === -1) {
				shapedExtraAddress = props.extraForwardingAddressData
					? {
							value: props.extraForwardingAddressData.addressId,
							label: props.extraForwardingAddressData.formatted,
							object: {
								addressId: props.extraForwardingAddressData.addressId,
								name: props.extraForwardingAddressData.name,
								businessName: props.extraForwardingAddressData.businessName,
								propertyName: props.extraForwardingAddressData.propertyName,
								addressLine1: props.extraForwardingAddressData.addressLine1,
								addressLine2: props.extraForwardingAddressData.addressLine2,
								addressLine3: props.extraForwardingAddressData.addressLine3,
								countryCode: props.extraForwardingAddressData.countryCode,
								emailAddress: '',
								phoneNo: '',
								postCode: props.extraForwardingAddressData.postCode,
							},
						}
					: props.extraAddressData;
			}
		}

		const newParsedData = shapedExtraAddress ? [shapedExtraAddress, ...newAddresses.flat()] : newAddresses.flat();

		setAddressOptions(newParsedData);

		if (props.defaultValues) {
			HandleItemsChanged(props.defaultValues[0], newParsedData);
		}
	};

	useEffect(() => {
		if (data) {
			MapAddressOptions(data);
		}
	}, [data, dataUpdatedAt, props.extraForwardingAddressData]);

	if (status === 'error') {
		return <AlertBanner title={error?.message} colour={Colour.Alert} />;
	}

	return (
		<StackVertical gap={FlexGap.ExtraSmall}>
			<ComboBoxControl
				name={props.name}
				placeholder={props.placeholder || t('controls.addressPlaceholder')}
				data={addressOptions}
				defaultValue={props.defaultValues ? props.defaultValues : undefined}
				loading={status === 'pending' || !addressOptions || props.loading}
				valueType='number'
				pagination={{
					hasNextPage,
					itemCounts: addressItemCounts,
					isStale,
					isRefetching,
					isFetchingNextPage,
					fetchNextPage,
				}}
				displayType={ComboBoxTypeEnum.Single}
				displayItemLabelType={ComboBoxItemDisplayEnum.Label}
				forwardItemCallback={(value) => HandleItemsChanged(value)}
				forwardValueCallback={(e) => HandleValueChanged(e)}
				forwardValuesCallback={(e) => HandleValueChanged(e)}
				formReset={props.reset}
				disabled={props.disabled}
			/>

			{props.showCreateAddressOptions && (
				<AddAddressTypeButtons
					{...props}
					addressAddedCallback={(e) => (props.newAddressCallback ? props.newAddressCallback(e) : null)}
					vcfImportedCallback={(e) => (props.bulkAddressCallback ? props.bulkAddressCallback(e) : null)}
					csvImportedCallback={(e) => (props.bulkAddressCallback ? props.bulkAddressCallback(e) : null)}
				/>
			)}
		</StackVertical>
	);
}

export { AddressControl };
